
import { computed, defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// validation
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { DirectionT, CompanyDirection } from '@/core/types/directions.types'
import { CompanyT } from '@/core/types/companies.types'

// realisations
import { directions } from '@/core/realisations/directions.realisation'
import { companies } from '@/core/realisations/companies.realisation'

export default defineComponent({
    components: {
        modal: Modal,
        'the-select': TheSelect,
        Checkbox
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<CompanyDirection>,
            default: null,
        },
    },
    emits: ['create', 'update', 'cancel', 'delete'],
    setup(props, { emit }) {
        const state = reactive({
            id: null as number | null,
            companyId: null as number | null,
            directionId: null as number | null,
            isActive: false
        })

        const state_form$ = useVuelidate({
            companyId: { required },
            directionId: { required }
        }, state)

        const data_state = reactive({
            directions: new DataState<DirectionT>({}),
            companies: new DataState<CompanyT>({}),
        })

        const loadDirections = async () => {
            try {
                data_state.directions.is_loading = true

                const response = await directions.getList({ page: 1, limit: 9999, isExtended: 0 })
                data_state.directions.list = response.data

                data_state.directions.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.directions.is_failed = true
            } finally {
                data_state.directions.is_loading = false
            }
        }

        const loadCompanies = async () => {
            try {
                data_state.companies.is_loading = true

                const response = await companies.getAllList({ page: 1, limit: 9999, isExtended: 0, getAll: 1 })
                data_state.companies.list = response.data

                data_state.companies.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.companies.is_failed = true
            } finally {
                data_state.companies.is_loading = false
            }
        }

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Добавление направления компании'
            if (props.isUpdate) return 'Редактирование направления компании'
            if (props.isDelete) return 'Удаление направления компании'
            return 'modal title'
        })

        const modalConfirmButtonText = computed(() => {
            if (props.isCreate) return 'Добавить'
            if (props.isUpdate) return 'Редактировать'
            if (props.isDelete) return 'Удалить'
            return 'Подтвердить'
        })

        const emitCreate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            const payload = {
                companyId: state.companyId,
                directionId: state.directionId,
                isActive: state.isActive
            }

            emit('create', payload)
        }

        const emitUpdate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            const payload = {
                companyId: state.companyId,
                directionId: state.directionId,
                isActive: state.isActive
            }

            emit('update', { id: props.initialValue.id, payload })
        }

        const emitDelete = () => {
            emit('delete', state.id)
        }

        const emitManager = () => {
            if (props.isCreate) emitCreate()
            if (props.isUpdate) emitUpdate()
            if (props.isDelete) emitDelete()
        }

        const emitCancel = () => {
            emit('cancel')
        }

        onBeforeMount(() => {
            loadDirections()
            loadCompanies()

            if (!props.initialValue) return

            state.id = props.initialValue.id
            state.directionId = props.initialValue.directionId
            state.companyId = props.initialValue.companyId
            state.isActive = props.initialValue.isActive
        })

        return {
            state,
            state_form$,
            data_state,
            modalTitle,
            modalConfirmButtonText,
            emitManager,
            emitCancel,
            loadDirections,
            loadCompanies,
        }
    },
})
